// SF1 — the historical calendar, SF4 — budget allocation, SF5 — conflicts.
//
// COLOUR IS THE VERDICT. A block is coloured by its measured net, never by
// redemptions: a promotion with heavy redemptions and a negative net reads as
// the loss it was. A calendar coloured by activity would show a busy,
// healthy-looking year that lost money.
//
// Spikes appear on the same axis in their own colour, so a strong period that
// was NOT a promotion is visible as such rather than being credited to whatever
// ran nearby.
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { FiAlertTriangle, FiInfo } from 'react-icons/fi';

import AuthContext from '../../../../context/AuthContext';
import local from '../PromoCalendar.module.css';
import { getBudget, getFestivals, getHistory } from '../services/calendarApi';

const inr = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

const BLOCK_CLASS = {
  paid: 'paidBlock', lost: 'lostBlock', spike: 'spikeBlock',
};

const CalendarView = () => {
  const authCtx = useContext(AuthContext);
  const token = authCtx?.token;
  const [history, setHistory] = useState(null);
  const [budget, setBudget] = useState(null);
  const [festivals, setFestivals] = useState(null);
  const [amount, setAmount] = useState(50000);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true); setError(null);
    try {
      const [h, b, f] = await Promise.all([
        getHistory(token), getBudget(token, { budget: amount }), getFestivals(token),
      ]);
      setHistory(h); setBudget(b); setFestivals(f);
    } catch (err) { setError(err); }
    finally { setLoading(false); }
  }, [token, amount]);
  useEffect(() => { load(); }, [load]);

  const blocks = history?.blocks || [];

  return (
    <>
      <section className={local.card}>
        <div className={local.cardHead}>
          <h2 className={local.cardTitle}>Everything you have run, and what it returned</h2>
          {history && (
            <span className={local.muted}>
              {history.promotions} promotions · {history.spikes} unexplained lifts
            </span>
          )}
        </div>
        <p className={local.muted}>
          Each block is coloured by what it actually netted, never by
          redemptions. Purple blocks are lift no promotion explains.
        </p>

        {error && (
          <div className={`${local.banner} ${error.reportUnavailable ? local.bannerWarn : local.bannerError}`}>
            <FiAlertTriangle className={local.bannerIcon} />
            <div>
              <strong>
                {error.notConnected ? 'No Amazon account connected' : 'Could not load the calendar'}
              </strong>
              <div>{error.message}</div>
            </div>
          </div>
        )}

        {blocks.length === 0 ? (
          <div className={local.emptyCell}>
            {loading ? 'Loading…' : 'No promotions or unexplained lifts found yet.'}
          </div>
        ) : (
          <div className={local.timeline}>
            {blocks.map((block) => (
              <div key={block.id}
                className={`${local.block} ${local[BLOCK_CLASS[block.verdict] || 'unknownBlock']}`}>
                <span className={local.blockDates}>{block.start} → {block.end}</span>
                <span>{block.name}</span>
                <span className={local.blockNet}>
                  {block.net !== null && block.net !== undefined
                    ? `${block.net >= 0 ? '+' : '−'}₹${inr.format(Math.abs(Math.round(block.net)))}`
                    : block.units_above_expected
                      ? `+${block.units_above_expected} units`
                      : '—'}
                </span>
              </div>
            ))}
          </div>
        )}

        {history?.notes?.length > 0 && (
          <ul className={local.noteList}>
            {history.notes.slice(0, 4).map((note) => (
              <li key={note}><FiInfo className={local.noteIcon} />{note}</li>
            ))}
          </ul>
        )}
      </section>

      <section className={local.card}>
        <div className={local.cardHead}>
          <h2 className={local.cardTitle}>Where a budget would go</h2>
          <label className={local.field}>
            <span>Budget (₹)</span>
            <input type="number" value={amount} min={0} step={5000}
              onChange={(e) => setAmount(Number(e.target.value))} />
          </label>
        </div>
        <p className={local.muted}>
          Ranked by expected net per rupee of cost, never by lift — a budget
          spent on the biggest lift buys the biggest discount, which is not the
          same as the most money.
        </p>

        {budget && (
          <>
            <div className={local.breadcrumb}>
              <span>Spent <strong>₹{inr.format(budget.spent)}</strong> of ₹{inr.format(budget.budget)}</span>
              <span className={local.crumbTotal}>
                Expected net <strong>₹{inr.format(budget.expected_net)}</strong>
              </span>
            </div>
            {budget.allocated.length === 0 ? (
              <div className={local.emptyCell}>
                Nothing has enough measured history to allocate against yet.
              </div>
            ) : (
              <div className={local.tableScroll}>
                <table className={local.table}>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>Product type</th>
                      <th className={local.numeric}>Cost</th>
                      <th className={local.numeric}>Expected net</th>
                      <th className={local.numeric}>Net per ₹</th>
                      <th>Basis</th>
                    </tr>
                  </thead>
                  <tbody>
                    {budget.allocated.map((row) => (
                      <tr key={`${row.type}-${row.product_type}`}>
                        <td>{row.type}</td>
                        <td>{row.product_type}</td>
                        <td className={local.numeric}>₹{inr.format(row.cost)}</td>
                        <td className={local.numeric}>₹{inr.format(row.expected_net)}</td>
                        <td className={local.numeric}>{row.net_per_rupee}</td>
                        <td className={local.muted}>{row.basis} · {row.runs} runs</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {budget.excluded.length > 0 && (
              <div className={local.coverage}>
                <strong>Excluded</strong>
                <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                  {budget.excluded.slice(0, 6).map((row, index) => (
                    <li key={index}>{row.type || 'Unknown'} — {row.reason}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </section>

      {festivals && !festivals.available && (
        <section className={local.card}>
          <div className={local.cardHead}>
            <h2 className={local.cardTitle}>Festival suggestions</h2>
            <span className={local.muted}>Not built yet</span>
          </div>
          <p className={local.muted}>{festivals.reason}</p>
          <ul className={local.noteList}>
            {festivals.requires.map((item) => (
              <li key={item}><FiInfo className={local.noteIcon} />{item}</li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
};

export default CalendarView;
